import { useAuthCtx } from '@/app/context/auth/authContextController/AuthContextController';
import { Button, buttonVariants } from '@/design-system/button';
import { usePermissions } from '@/features/auth/access-control/context/PermissionsProvider';
import {
  getFirstAccessibleChild,
  getFirstAccessibleRoute,
} from '@/features/auth/access-control/getFirstAccessibleChild';
import { SECTION_CHILD_PATHS } from '@/features/navigation/sidebar/sidebar.config';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ShieldAlert } from 'lucide-react';
import { z } from 'zod';

const forbiddenSearchSchema = z.object({
  from: z.string().optional(),
});

export const Route = createFileRoute('/(app)/forbidden')({
  validateSearch: forbiddenSearchSchema,
  component: ForbiddenPage,
});

function ForbiddenPage() {
  const { from } = Route.useSearch();
  const perms = usePermissions();
  const { logout } = useAuthCtx();
  const sectionPath = Object.keys(SECTION_CHILD_PATHS).find(
    (section) => from === section || from?.startsWith(`${section}/`),
  );
  const sectionDestination = sectionPath ? getFirstAccessibleChild(sectionPath, perms) : undefined;
  const homeDestination = getFirstAccessibleRoute(perms) ?? '/';

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="flex max-w-lg flex-col items-center text-center">
        <div className="mb-6 flex size-16 items-center justify-center rounded-full bg-destructive/10">
          <ShieldAlert className="size-8 text-destructive-text" />
        </div>

        <p className="mb-2 text-sm font-semibold text-destructive-text">403 — Access denied</p>
        <h1 className="text-2xl font-bold text-foreground-heading">You do not have permission to view this page</h1>
        <p className="mt-3 text-sm text-foreground-caption">
          Contact an administrator if you believe your role should have access.
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {sectionDestination && (
            <Link className={buttonVariants()} to={sectionDestination}>
              Go to an available page in this section
            </Link>
          )}
          <Link
            className={buttonVariants({ variant: sectionDestination ? 'outline' : 'default' })}
            to={homeDestination}
          >
            Go to home
          </Link>
          <Button variant="destructive" onClick={() => void logout({})}>
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
}
